/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  TableContainer,
  Link,
  Tooltip,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import moment from 'moment';
import { Link as RouterLink } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { DownloadOutlined } from '@mui/icons-material';
import { PDFDownloadLink } from '@react-pdf/renderer';
import {
  getTransactionPaymentMethodLabel,
  getTransactionProcessor,
  transformTransactionToInvoice,
} from 'renderer/features/payments-page/schemas/transaction.transformer';
import TransactionProcessorIcon from 'renderer/features/payments-page/components/TransactionProcessorIcon';
import { TransactionDetails } from 'renderer/features/payments-page/schemas/transaction.schemas';
import { formatCurrency } from 'renderer/shared/formatters/formatters';
import _ from 'lodash';
import ExportPDFView from './export-pdf';

const tableStyles = {
  tableContainer: {
    // marginTop: '20px',
    marginBottom: '20px',
    border: '1px solid rgba(224, 224, 224, 1)',
  },
  table: {
    minWidth: 650,
  },
  tableHead: {
    backgroundColor: '#f5f5f5',
  },
  tableCell: {
    borderBottom: '1px solid rgba(224, 224, 224, 1)',
  },
  tableRow: {
    '&:last-child td': {
      border: 0,
    },
  },
};

const PDFIconButton = ({
  transactionGroup,
  isFailed,
}: {
  transactionGroup: TransactionDetails[];
  isFailed?: boolean;
}) => {
  const theme = useTheme();

  if (isFailed) {
    return (
      <Tooltip title="Unavailable for failed payments" placement="top">
        <span>
          <IconButton disabled>
            <DownloadOutlined style={{ color: theme.palette.grey[400] }} />
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  const transactionInfo = transformTransactionToInvoice(transactionGroup);
  return (
    <PDFDownloadLink
      document={<ExportPDFView transaction={transactionInfo} />}
      fileName={`${transactionInfo.id}.pdf`}
    >
      <Tooltip title="Download" placement="top">
        <IconButton>
          <DownloadOutlined style={{ color: theme.palette.grey[900] }} />
        </IconButton>
      </Tooltip>
    </PDFDownloadLink>
  );
};

interface PaymentsProps {
  payments: TransactionDetails[];
  handleOpenModal: (serials: string[]) => void;
  handleOpenProductModal: (products: string[]) => void;
  isAdmin?: boolean;
}

const Payments: React.FC<PaymentsProps> = ({
  payments,
  handleOpenModal,
  handleOpenProductModal,
  isAdmin = false,
}) => {
  const groupPayments = (data: TransactionDetails[]) => {
    // Group multi-product checkouts by cartId, but keep failed attempts as their
    // own rows — retries reuse the same open cart and must not merge with success.
    const grouped = _.groupBy(data, (p) =>
      p.status === 'failed' || !p.cartId ? p._id.toString() : p.cartId,
    );

    return Object.values(grouped).map((group) => {
      const first = group[0];
      return {
        ...first,
        originalGroup: group, // pass this down
        isGrouped: group.length > 1,
        groupedProducts: group.map((t) => t.productName),
        groupedCategories: _.uniq(group.map((t) => t.category)),
        groupedSerials: group.flatMap((t) => t.serials),
        groupedTotal: _.sumBy(group, (t) => t.amount),
      };
    });
  };

  return (
    <TableContainer component={Paper} sx={tableStyles.tableContainer}>
      <Table sx={tableStyles.table} aria-label="licenses table" size="small">
        <TableHead sx={tableStyles.tableHead}>
          <TableRow>
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
              PRODUCT
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
              STATUS
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
              PAYMENT METHOD
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
              CATEGORY
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
              DATE
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
              UNIT PRICE
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
              QUANTITY
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
              TOTAL
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
              PDF
            </TableCell>
            {isAdmin && (
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                DETAILS
              </TableCell>
            )}
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
              LICENSE KEY
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groupPayments(payments).map((payment) => {
            const processor = getTransactionProcessor(payment);
            const paymentMethodLabel =
              getTransactionPaymentMethodLabel(payment);

            return (
              <TableRow key={payment._id}>
                <TableCell align="center">
                  {payment.groupedProducts[0]}
                  {payment.isGrouped && payment.groupedProducts && (
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() =>
                        handleOpenProductModal(
                          payment.groupedProducts.filter(
                            (p): p is string => typeof p === 'string',
                          ),
                        )
                      }
                      sx={{ textDecoration: 'underline', ml: 1 }}
                    >
                      +{payment.groupedProducts.length - 1}
                    </Link>
                  )}
                </TableCell>
                <TableCell align="center">
                  <span
                    style={{
                      color:
                        payment.status === 'failed'
                          ? 'red'
                          : payment.status === 'succeeded'
                          ? 'green'
                          : 'inherit',
                      fontWeight: 'bold',
                    }}
                  >
                    {payment.status?.toUpperCase() || 'UNKNOWN'}
                  </span>
                </TableCell>

                <TableCell align="center">
                  <Box
                    sx={{
                      display: 'inline-flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 0.5,
                      justifyContent: 'center',
                    }}
                  >
                    {processor && (
                      <TransactionProcessorIcon
                        processor={processor}
                        height={12}
                      />
                    )}
                    <span>{paymentMethodLabel ?? '—'}</span>
                  </Box>
                </TableCell>

                <TableCell align="center">
                  {/* {payment.groupedCategories.length > 1
                  ? 'Plugin and Subscription'
                  : payment.groupedCategories[0]} */}
                  {payment.groupedCategories.length > 1
                    ? payment.groupedCategories.includes('Bundle') &&
                      payment.groupedCategories.includes('Subscription')
                      ? 'Bundle and Subscription'
                      : payment.groupedCategories.includes('Bundle')
                      ? 'Bundle'
                      : payment.groupedCategories.includes('Subscription')
                      ? 'Subscription'
                      : 'Plugin'
                    : payment.groupedCategories[0]}
                </TableCell>

                <TableCell align="center">
                  {moment(payment.createdAt).format('MM-DD-YYYY')}
                </TableCell>

                <TableCell align="center">
                  {payment.isGrouped
                    ? '-'
                    : `$${
                        payment.amount /
                        (payment.quantity ? payment.quantity : 1)
                      }`}
                </TableCell>

                <TableCell align="center">{payment.quantity}</TableCell>

                <TableCell align="center">
                  {formatCurrency(payment.groupedTotal)}
                </TableCell>

                <TableCell align="center">
                  <PDFIconButton
                    transactionGroup={payment.originalGroup}
                    isFailed={payment.status === 'failed'}
                  />
                </TableCell>

                {isAdmin && (
                  <TableCell align="center">
                    <Tooltip title="View transaction details">
                      <IconButton
                        component={RouterLink}
                        to={`/transactions/${payment._id}`}
                        size="small"
                        color="info"
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}

                <TableCell align="center">
                  <Tooltip title="View all serials">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenModal(payment.groupedSerials)}
                      sx={{ ml: 1 }}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default Payments;
